/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './AccountVisibilityTable.scss';

import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import Store from 'state/Store';

import {
  getCustomizeAccountsViewTableData,
  getAreAllAccountsHidden
} from 'state/preferences/PreferencesSelectors';
import { constructAccountVisibilityPayload } from 'services/Preferences';
import {
  hideShowUpdated,
  resetAccountVisibility,
  accountVisibilityUpdated,
  errorUpdateAccountVisibility
} from 'state/preferences/PreferencesActions';
import { getReceivedAccounts } from 'state/account/AccountSelectors';
import { tableTypes } from 'services/TableSupport';
import Optioner from 'components/shared/Optioner/Optioner';
import ToggleBar from 'components/shared/ToggleBar/ToggleBar';
import { updateCustomerPreferences } from 'services/Rest';
import MobileToggleSwitch from 'components/shared/Toggle/MobileToggleSwitch';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import SaveLoadingButtonWithPermissions from 'components/shared/LoadingButton/SaveLoadingButtonWithPermissions';

const isParentRow = (row) => row._type === tableTypes.HEADER_ROW;

const toggleOptions = [
  {
    label: 'Show',
    value: false
  },
  {
    label: 'Hide',
    value: true
  }
];

const AccountNumber = ({ accountWChecksum }) => (
  <Fragment>{accountWChecksum}</Fragment>
);

const AccountInfo = ({ accountWChecksum, owners }) => (
  <Fragment>
    <div>
      {accountWChecksum}
      <span className="spacing-dot" />
      <span>{owners}</span>
    </div>
  </Fragment>
);

const AccountNumberWithPermissions = WithUserPermissions(AccountNumber);
const AccountInformationWithPermissions = WithUserPermissions(AccountInfo);

class AccountVisibilityTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      changesSaved: false
    };
  }

  componentWillUnmount() {
    const { changesSaved } = this.state;
    if (!changesSaved) {
      Store.dispatch(resetAccountVisibility());
    }
  }

  saveHideShowValues = () => {
    const { accounts, defaultAccounts } = this.props;
    const payload = constructAccountVisibilityPayload(
      accounts,
      defaultAccounts
    );
    const updatedHideShowAccountsPayload = {
      accountVisibility: [...payload]
    };
    updateCustomerPreferences(
      updatedHideShowAccountsPayload,
      () => {Store.dispatch(accountVisibilityUpdated())},
      () => {Store.dispatch(errorUpdateAccountVisibility())}
    );
    this.setState({ changesSaved: true });
  };

  render() {
    const {
      accountGroups,
      isDesktopLayout,
      disableSave,
      allAccountsHidden,
      hasAlaska529Only
    } = this.props;
    return (
      <div className="account-visibility-table-container">
        {accountGroups.map((group, i) => (
          <table className="account-visibility-table" key={i}>
            <tbody>
              <TableRows
                rows={group}
                key={i}
                isDesktopLayout={isDesktopLayout}
                hasAlaska529Only={hasAlaska529Only}
              />
            </tbody>
          </table>
        ))}
        {isDesktopLayout && (
          <div>
            {allAccountsHidden && (
              <div className="hide-all-disclosure">
                You must show at least one investment
              </div>
            )}
            <SaveLoadingButtonWithPermissions
              className="customize-accounts-save-button account-visibility-button"
              id="save-account-visibility"
              onClick={this.saveHideShowValues}
              disabled={disableSave || allAccountsHidden}
              requestStatusKey="updateCustomerPreferences"
              shouldDisable>
              Save
            </SaveLoadingButtonWithPermissions>
          </div>
        )}
      </div>
    );
  }
}

const TableRows = ({
  rows,
  parentKey = '',
  isDesktopLayout,
  hasAlaska529Only
}) => combineRows(rows, parentKey, isDesktopLayout, hasAlaska529Only);

const combineRows = (
  rows = [],
  parentKey = '',
  isDesktopLayout,
  hasAlaska529Only
) => {
  return [].concat(rows).reduce((acc, row, childKey) => {
    const key = parentKey + ':' + childKey;

    return acc
      .concat(
        <Row
          key={key}
          details={row.details}
          isParentRow={isParentRow(row)}
          isDesktopLayout={isDesktopLayout}
          hasAlaska529Only={hasAlaska529Only}
        />
      )
      .concat(
        row._type === tableTypes.ACCOUNT_ROW
          ? []
          : combineRows(row.children, key, isDesktopLayout, hasAlaska529Only)
      );
  }, []);
};

const Row = ({
  details = {},
  isParentRow,
  isDesktopLayout,
  hasAlaska529Only
}) => {
  return (
    <Optioner val={{ isParentRow }}>
      {{
        isParentRow: () => (
          <TableHeaderRow
            details={details}
            hasAlaska529Only={hasAlaska529Only}
          />
        ),
        _: () => (
          <TableBodyRows details={details} isDesktopLayout={isDesktopLayout} />
        )
      }}
    </Optioner>
  );
};

const TableHeaderRow = ({ details, hasAlaska529Only }) => {
  const { name } = details;
  const headerGroupLabel =
    hasAlaska529Only && name.groupLabel ? '529 Plans' : name.groupLabel;

  return (
    <tr
      className={`account-visibility-parent ${name.groupingClassName} header`}>
      <td colSpan="3" className="account-visibility-parent-header">
        {name.groupLabel.accountType || headerGroupLabel}
      </td>
    </tr>
  );
};

const TableBodyRows = ({ details, isDesktopLayout }) => {
  return (
    <tr className="account-visibility-subgroup-row">
      <td className="account-visibility-account-details-column">
        <AccountDetails details={details} isDesktopLayout={isDesktopLayout} />
        {!isDesktopLayout && details.marketValue === 0 && (
          <div className="account-visibility-account-balance">
            Account has $0 balance
          </div>
        )}
      </td>
      {isDesktopLayout && details.marketValue === 0 ? (
        <td className="account-visibility-account-balance-column">
          <div className="account-visibility-account-balance">
            Account has $0 balance
          </div>
        </td>
      ) : (
        <td />
      )}
      <td className="account-visibility-toggle-column">
        <HideShow details={details} isDesktopLayout={isDesktopLayout} />
      </td>
    </tr>
  );
};

const hideShowUpdate = (event, details) => {
  const hiddenValue = event.target.value === 'true' ? true : false;
  details.hiddenAccount = hiddenValue;
  Store.dispatch(hideShowUpdated(details));
};

const hideShowUpdateMobile = (details) => {
  details.hiddenAccount = !details.hiddenAccount;
  Store.dispatch(hideShowUpdated(details));
};

const HideShow = ({ details, isDesktopLayout }) => {
  const className = `account-visibility-toggle ${details.name.accountId}`;
  return (
    <Fragment>
      {isDesktopLayout && (
        <ToggleBar
          className={className}
          onChange={(event) => hideShowUpdate(event, details)}
          options={toggleOptions}
          selection={details.hiddenAccount}
        />
      )}
      {!isDesktopLayout && (
        <div className="mobile-account-visibility-toggle-switch">
          <MobileToggleSwitch
            onChange={() => hideShowUpdateMobile(details)}
            options={toggleOptions}
            id={details.name.accountId}
            selection={!details.hiddenAccount}
          />
        </div>
      )}
    </Fragment>
  );
};

const AccountDetails = ({ details, isDesktopLayout }) => {
  return (
    <div className="account-visibility-account-details">
      {details.name.accountName && (
        <div>
          <div className="account-visibility-account-name">
            {details.name.accountName}
            {details.name.ticker && isDesktopLayout && (
              <span className="account-visibility-account-ticker">
                {details.name.ticker}
              </span>
            )}
          </div>
          <div className="account-visibility-account-info">
            {details.name.ticker && !isDesktopLayout && (
              <Fragment>
                <span className="account-visibility-account-ticker">
                  {details.name.ticker}
                </span>
                <span className="spacing-dot" />
              </Fragment>
            )}
            {!isDesktopLayout && (
              <AccountNumberWithPermissions
                accountWChecksum={details.name.accountWChecksum}
                shouldMask
              />
            )}
            {isDesktopLayout && (
              <Fragment>
                <AccountInformationWithPermissions
                  accountWChecksum={details.name.accountWChecksum}
                  owners={details.name.ownerNames}
                  shouldMask
                />
              </Fragment>
            )}
          </div>
        </div>
      )}
      {!details.name.accountName && <div>N/A</div>}
    </div>
  );
};

const mapStateToProps = (state) => ({
  isDesktopLayout: state.view.isDesktopLayout,
  accounts: state.accounts,
  defaultAccounts: getReceivedAccounts(state),
  accountGroups: getCustomizeAccountsViewTableData(state),
  disableSave: state.disableAccountVisibilitySave,
  allAccountsHidden: getAreAllAccountsHidden(state),
  hasAlaska529Only: state.view.hasAlaska529Only
});

export default connect(mapStateToProps)(AccountVisibilityTable);
